import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SuccessScreen from '@/components/ui/SuccessScreen/SuccessScreen';
import { getOnlineDonationStatus } from './api';
import styles from './PublicDonation.module.css';

interface Props {
  outcome?: 'success' | 'failure';
}

const POLL_INTERVAL = 3000;
const POLL_MAX_ATTEMPTS = 40;

const DonationResultPage = ({ outcome: fixedOutcome }: Props) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const lang = searchParams.get('lang');
  const statusParam = searchParams.get('status');
  const trackingId = searchParams.get('trackingId');
  const fundId = searchParams.get('fundId');

  const [pollResult, setPollResult] = useState<'success' | 'failure' | null>(null);

  useEffect(() => {
    if (lang && lang !== i18n.language) i18n.changeLanguage(lang);
  }, [lang, i18n]);

  useEffect(() => {
    if (fixedOutcome || !trackingId || statusParam === 'failure') return;

    let attempts = 0;
    let cancelled = false;

    const poll = async () => {
      while (!cancelled && attempts < POLL_MAX_ATTEMPTS) {
        attempts++;
        try {
          const status = await getOnlineDonationStatus(trackingId);
          if (status === 'Completed') {
            setPollResult('success');
            return;
          }
          if (status === 'Failed') {
            setPollResult('failure');
            return;
          }
        } catch { /* keep polling */ }
        await new Promise((r) => setTimeout(r, POLL_INTERVAL));
      }
      if (!cancelled) setPollResult('success');
    };

    poll();
    return () => { cancelled = true; };
  }, [fixedOutcome, trackingId, statusParam]);

  const outcome = fixedOutcome ?? (statusParam === 'failure' ? 'failure' : pollResult);

  if (!outcome) {
    return (
      <div className={styles.pageStandalone}>
        <div className={styles.card}>
          <div style={{ padding: '48px 0', textAlign: 'center' }}>
            <p>{t('donations.verifyingPayment')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (outcome === 'success') {
    return (
      <div className={styles.pageStandalone}>
        <div className={styles.card}>
          <div style={{ padding: '48px 0' }}>
            <SuccessScreen
              title={t('donations.success.title')}
              description={t('donations.success.description')}
              primaryButtonText={fundId ? t('donations.success.donateAgain') : undefined}
              primaryButtonAction={fundId ? () => navigate(`/donate/${fundId}`) : undefined}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.pageStandalone}>
      <div className={styles.card}>
        <h1 className={styles.title}>{t('donations.failure.title')}</h1>
        <p className={styles.subtitle}>{t('donations.failure.description')}</p>
      </div>
    </div>
  );
};

export default DonationResultPage;
